import React, { useState } from 'react'

import { NavLink as RouterNavLink, NavLinkProps, useParams } from 'react-router-dom'

import { useQueryPaginator } from 'src/components/Table'
import { PlaceDetailTable } from 'src/components/Table/detail/Place'
import { PlaceDialog } from 'src/modules/supply_chain/components/PlaceDialog'
import { ExportDialog } from 'src/components/Dialog'
import { HierarchyDetail } from 'src/modules/supply_chain/components/HierarchyDetail'
import { BarcodedUnitListTable } from 'src/components/Table/list/BarcodedUnit'
import { ContainerListTable } from 'src/components/Table/list/Container'
import { EventListTable } from 'src/components/Table/list/Event'
import { FoundListTable } from 'src/components/Table/list/Found'
import { LostListTable } from 'src/components/Table/list/Lost'
import { UnitListTable } from 'src/components/Table/list/Unit'
import { HierarchyPath } from 'src/components/Tree'
import { DamagedListTable } from 'src/components/Table/list/Damaged'

import { skuit } from 'src/services/skuit'
import { Place } from 'src/api/models'
import { useQuery } from 'react-query'

import { DetailPage } from 'src/components/Pages/DetailPage'

const NavLink = React.forwardRef((props: NavLinkProps, ref: React.Ref<HTMLAnchorElement>) => (
  <RouterNavLink innerRef={ref} {...props} />
))
NavLink.displayName = 'NavLink'


export const PlacePage = () => {
  const { guid } = useParams()
  const [exportOpen, setExportOpen] = useState(false)

  const place = useQuery<Place>(['place-details', guid], () => skuit.sc.places.retrieve(guid))
  const hierarchy = useQuery(['place-hierarchy', guid], () => skuit.sc.places.hierarchy(guid))

  const containerPaginator = useQueryPaginator(
    'container-list',
    skuit.sc.containers.list,
    { params: { page_size: 5, places: guid } }
  )
  const deeperContainerPaginator = useQueryPaginator(
    'container-descendant-list',
    skuit.sc.containers.list,
    { params: { page_size: 5, ancestors: guid } }
  )
  const unitsPaginator = useQueryPaginator(
    'unit-list',
    skuit.sc.units.list,
    {
      params: { page_size: 5, places: guid },
    }
  )
  const lostPaginator = useQueryPaginator(
    'lost-list',
    skuit.sc.lost.list,
    {
      params: { page_size: 5, places: guid },
    }
  )
  const foundPaginator = useQueryPaginator(
    'found-list',
    skuit.sc.found.list,
    {
      params: { page_size: 5, places: guid },
    }
  )
  const deeperUnitsPaginator = useQueryPaginator(
    'unit-descendant-list',
    skuit.sc.units.list,
    {
      params: { page_size: 5, ancestors: guid },
    }
  )
  const barcodedPaginator = useQueryPaginator(
    'barcoded-unit-list',
    skuit.sc.barcodedUnits.list,
    { params: { page_size: 5, places: guid } }
  )
  const damagedPaginator = useQueryPaginator('damaged-list', skuit.sc.damaged.list, {
    params: { page_size: 5, places: guid },
  })
  const eventsPaginator = useQueryPaginator(
    'event-list',
    skuit.sc.events.list,
    {
      params: { page_size: 5, references: guid },
    }
  )

  const refresh = () => {
    place.refetch()
    hierarchy.refetch()
    containerPaginator.refresh()
    deeperContainerPaginator.refresh()
    unitsPaginator.refresh()
    deeperUnitsPaginator.refresh()
    lostPaginator.refresh()
    foundPaginator.refresh()
    barcodedPaginator.refresh()
    eventsPaginator.refresh()
  }

  const exportFilters = {"guid": guid}

  return (
    <>
      {exportOpen && (
        <ExportDialog
          name="Inventory Report"
          download={skuit.sc.facilities.exportInventory}
          open={exportOpen}
          fields={['sku', 'num_units', 'place']}
          exportFilters={exportFilters}
          handleClose={() => setExportOpen(false)}
        />
      )}
      <DetailPage
        obj={place}
        title="Place"
        refresh={refresh}
        breadcrumbs={[
          { title: 'Dashboard', url: '/' },
          { title: 'Places', url: '/warehouse/places' },
        ]}
        detailComponent={<PlaceDetailTable obj={place} refresh={refresh} />}
        createDialog={{ title: 'New Place', Dialog: PlaceDialog }}
      >
        <HierarchyPath title="Storage Path" location={place} />
        <HierarchyDetail title="Place Hierarchy" roots={hierarchy.data || []} />
        <ContainerListTable
          paginator={containerPaginator}
          title="Containers in Place"
          fields={['name', 'containerType', 'path']}
        />
        <ContainerListTable
          paginator={deeperContainerPaginator}
          title="Containers in Children"
          fields={['name', 'containerType', 'path']}
        />
        <UnitListTable
          paginator={unitsPaginator}
          title="Units in Place"
          fields={['productName', 'product', 'sku', 'batch', 'client', 'numUnits', 'path']}
        />
        <UnitListTable
          paginator={deeperUnitsPaginator}
          title="Units in Children"
          fields={['productName', 'product', 'sku', 'batch', 'client', 'numUnits', 'path']}
        />
        <BarcodedUnitListTable
          paginator={barcodedPaginator}
          title="Barcoded Units in Place"
          fields={['guid', 'client', 'product']}
        />
        <LostListTable
          paginator={lostPaginator}
          title="Lost Here"
          fields={['sku', 'client', 'product', 'batch', 'numUnits', 'dateReceived', 'path']}
        />
        <FoundListTable
          paginator={foundPaginator}
          title="Found Here"
          fields={['sku', 'client', 'product', 'batch', 'numUnits', 'path']}
        />
        <DamagedListTable
          paginator={damagedPaginator}
          fields={['guid', 'client', 'product', 'sku', 'batch']}
        />
        <EventListTable
          paginator={eventsPaginator}
          title="Place Events"
          fields={['type', 'data', 'by', 'on']}
        />
      </DetailPage>
    </>
  )
}
